import React, { useCallback, useLayoutEffect, useMemo, useState } from 'react';
import { StyleSheet } from 'react-native';
import Button from 'design-system/Button';
import SuccessView from 'design-system/SuccessView';
import StatusBar from 'design-system/StatusBar';
import { ButtonGroup } from 'design-system/ButtonGroup';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import { selectUserId } from 'reducers/selectors';
import { muteErrorAndResult, useFetchOld, useTimeout } from 'hooks';
import Squircle from 'design-system/Squircle';
import { Tier } from 'features/premium/entities';
import IconCheck from 'design-system/icons/IconCheck';
import useColors from 'hooks/useColors';
import AnimatedIconBell from 'features/pay/icons/AnimatedIconBell';
import iconSource from 'design-system/img/notifications.png';
import PaymentToastModal from 'features/pay/components/toast/PaymentToastModal';
import { AppView, rem } from 'design-system';
import { navigationRef, popToTop } from 'utils/navigationv6';
import EmptyComponent from 'design-system/EmptyComponent';
import { benefits } from 'features/premium/benefits';
import { UpgradeWebModal } from 'design-system/UpgradeWebModal';
import { selectDefaultSpaceId, selectSelectedSpaceInfo, } from './reducers/selectors';
import { sendSpaceReminder, setSelectedSpace, setShowSpaceLock, } from './actions';
import { isWeb } from '../../constants';
const SpaceUnavailableLeaveAccountsScreen = React.memo(({ navigation, route }) => {
    const { spaceInfo } = route.params;
    const colors = useColors();
    const userId = useAppSelector(selectUserId);
    const defaultSpaceId = useAppSelector(selectDefaultSpaceId);
    const selectedSpaceInfo = useAppSelector(selectSelectedSpaceInfo);
    const dispatch = useAppDispatch();
    const isInAvailableSpace = selectedSpaceInfo?.space.isPremium || selectedSpaceInfo?.space.isDefault;
    const isOwner = spaceInfo.space.createdBy === userId;
    useLayoutEffect(() => {
        navigation.setOptions({
            title: 'Move accounts',
            headerTitle: '',
            headerLeft: EmptyComponent,
            gestureEnabled: false,
            headerBackVisible: false,
        });
    }, []);
    const [, , isSendingReminder, fetchSendReminder] = useFetchOld(muteErrorAndResult);
    const [showToast, setShowToast] = useState(false);
    useTimeout(() => setShowToast(false), showToast ? 3000 : undefined);
    const onSendReminder = useCallback(async () => {
        const res = await fetchSendReminder(sendSpaceReminder(spaceInfo.space.id, 'SPACE_UPGRADE_REMINDER'));
        if (!res)
            return;
        setShowToast(true);
    }, [fetchSendReminder, spaceInfo.space.id]);
    const title = useMemo(() => {
        if (isOwner) {
            return 'We’ve kept your accounts in your Spaces';
        }
        return `We’ve kept your accounts in the space "${spaceInfo.space.name}"`;
    }, [isOwner, spaceInfo.space.name]);
    const subtitle = useMemo(() => {
        if (isOwner) {
            return 'You can manage the accounts you left in Spaces anytime from the Manage spaces screen. To start using Spaces again, simply upgrade your plan whenever you’re ready.';
        }
        return 'You can manage the accounts you left in spaces anytime from the Manage spaces screen. To use this space again, contact the owner to upgrade their plan.';
    }, [isOwner]);
    const onSwitch = useCallback(() => {
        if (!defaultSpaceId)
            return;
        dispatch(setSelectedSpace(defaultSpaceId));
        navigation.navigateDeprecated('Success', {
            topTitle: 'App changed to:',
            title: 'Private Space',
            subTitle: 'Only assets that you have in this space will be visible in Feed.',
            buttonText: 'Done',
            onContinue: () => {
                popToTop();
                navigationRef.navigateDeprecated('Feed');
                dispatch(setShowSpaceLock(false));
                return true;
            },
        });
    }, [defaultSpaceId]);
    const onDone = useCallback(() => {
        navigation.pop();
        dispatch(setShowSpaceLock(false));
    }, []);
    const onUpgrade = useCallback(() => {
        if (isWeb && benefits.spaces.minTier !== Tier.free) {
            UpgradeWebModal.show(benefits.spaces.minTier);
        }
        else {
            navigation.navigateDeprecated('EmmaProModal', {
                benefitId: 'spaces',
                tier: Tier.ultimate,
                isTierRequired: true,
            });
        }
    }, []);
    const owner = useMemo(() => spaceInfo.users?.find(({ id }) => id === spaceInfo.space.createdBy), [spaceInfo.space.createdBy, spaceInfo.users]);
    return (<AppView withFrame={false}>
        <PaymentToastModal isVisible={showToast} onCloseAction={() => setShowToast(false)} title="Sent a reminder to upgrade" message={`To ${owner?.firstName || 'owner'}`} iconSource={iconSource} style={styles.toast}/>
        <StatusBar barStyle="dark-content" animated/>
        <SuccessView title={title} subTitle={subtitle} icon={<Squircle width={88} height={88} color={colors.elements.statusPositiveLight}>
              <IconCheck thick width={54} height={54} color={colors.elements.statusPositive}/>
            </Squircle>}>
          <ButtonGroup type="floating">
            <Button title={isOwner
            ? 'Upgrade to use Spaces again'
            : 'Remind owner to renew'} leftIcon={isOwner ? null : (<AnimatedIconBell isFetching={isSendingReminder} tintColor={colors.text.textOnBrand}/>)} onPress={isOwner ? onUpgrade : onSendReminder} brandReversed/>
            <Button title={isInAvailableSpace ? 'Done' : 'Switch to Private Space'} onPress={isInAvailableSpace ? onDone : onSwitch} brand/>
          </ButtonGroup>
        </SuccessView>
      </AppView>);
});
const styles = StyleSheet.create({
    toast: {
        marginTop: rem(56),
    },
});
export default SpaceUnavailableLeaveAccountsScreen;
